import { ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.tsx'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar.tsx'

/**
 * Navigation item interface for the main navigation component.
 */
interface NavMainItem {
  /** Display title for the navigation item */
  title: string
  /** URL to navigate to when clicked */
  url: string
  /** Optional Lucide icon component */
  icon?: LucideIcon
  /** Optional Tailwind color class for the icon */
  iconColor?: string
  /** Whether this item is currently active/selected */
  isActive?: boolean
  /** Optional count to display; zero is hidden */
  count?: number
  /**
   * 'brand' shows the count as a terracotta badge (reserved for what needs
   * attention, e.g. Inbox); default is a quiet number.
   */
  countTone?: 'brand' | 'muted'
  /** Sub-navigation items for collapsible sections */
  items?: Array<{
    title: string
    url: string
  }>
}

/**
 * Main navigation component that renders hierarchical navigation menus.
 *
 * Supports both flat navigation items and collapsible sub-menus.
 * Includes active state highlighting, count badges, and keyboard navigation.
 *
 * Features:
 * - Collapsible sub-navigation with smooth animations
 * - Active state management with visual highlighting
 * - Count badges for displaying item counts (e.g., unread notifications)
 * - Icon support with Lucide icons
 * - Accessible navigation with proper ARIA attributes
 *
 * @param items - Array of navigation items to render
 *
 * @example
 * ```tsx
 * const navigationItems = [
 *   {
 *     title: 'Inbox',
 *     url: '/tasks',
 *     icon: Inbox,
 *     isActive: true,
 *     count: 5
 *   },
 *   {
 *     title: 'Projects',
 *     url: '/projects',
 *     icon: FolderIcon,
 *     items: [
 *       { title: 'Project A', url: '/projects/a' },
 *       { title: 'Project B', url: '/projects/b' }
 *     ]
 *   }
 * ];
 *
 * <NavMain items={navigationItems} />
 * ```
 */
export function NavMain({ items }: { items: Array<NavMainItem> }) {
  return (
    <SidebarGroup className="px-2 py-1.5 group-data-[collapsible=icon]:px-0">
      <SidebarMenu className="gap-px">
        {items.map((item) =>
          item.items && item.items.length > 0 ? (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={item.isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={item.isActive}
                  >
                    {item.icon && <item.icon className={item.iconColor} />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild>
                          <Link to={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                tooltip={item.title}
                asChild
                isActive={item.isActive}
                className="h-8 rounded-md px-2.5 data-[active=true]:shadow-[0_0_0_1px_var(--sidebar-border),var(--shadow-2xs)]"
              >
                <Link
                  to={item.url}
                  aria-current={item.isActive ? 'page' : undefined}
                >
                  {item.icon && (
                    <item.icon className={cn('text-muted-foreground', item.iconColor)} />
                  )}
                  <span className="mr-auto">{item.title}</span>
                  {item.count ? (
                    item.countTone === 'brand' ? (
                      <span className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 text-[10.5px] font-semibold text-brand-foreground">
                        {item.count}
                      </span>
                    ) : (
                      <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                        {item.count}
                      </span>
                    )
                  ) : null}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
